import { useCallback, useMemo, useState } from 'react';
import { AnyObjectSchema } from 'yup';
import { GridColDef, GridValidRowModel, useGridApiRef } from '@mui/x-data-grid-pro';
import { mapGridIds, newGridRecordId, removeExtraSpaces } from '@shared/commonFunctions';
import { IPhoenixGridActions, IPhoenixGridProps, PhoenixGrid } from '@shared/components/grid/PhoenixGrid';
import { AddButton } from '@styles/ContactManager/ContactStyles';
import { useAzureMaps } from '@shared/hooks/useAzureMaps';
import { AlertModal } from '@shared/components/modals/AlertModal';

interface IContactSectionProps<T> extends Partial<IPhoenixGridProps> {
  data: T[];
  validateSchema?: AnyObjectSchema;
  strict?: boolean;
  name: string;
  columns: GridColDef[];
  actions?: IPhoenixGridActions;
  mutateCombinedDti?: () => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onChange: (data: T[]) => any;
}

export const ContactSection = <T,>({
  strict,
  name,
  data = [],
  columns,
  validateSchema,
  mutateCombinedDti,
  onChange,
  ...rest
}: IContactSectionProps<T>) => {
  const apiRef = useGridApiRef();
  const { getCounty } = useAzureMaps(); 
  
  const isAddress = name === 'Address';
  const isEmployment = name === 'Employment';
  const currentText = `${isAddress ? 'Current Address' : 'Primary Employment'}`;
  const AlertText = `${currentText} already exists. Do you want to make this the ${currentText}?`;

  const [openAlert, setOpenAlert] = useState(false);
  const [currentRow, setCurrentRow] = useState<GridValidRowModel>();

  const rows = useMemo(() => mapGridIds(data), [data]);

  const gridActions = useMemo(
    () => ({
      onDelete: async() => {
        const records = Array.from(apiRef.current.getRowModels().values()) as T[];
        const apiResponse = await onChange(records);
        mutateCombinedDti?.();
        return apiResponse;
      },

      onSave: async (row: GridValidRowModel) => {
        setCurrentRow(row);

        if (isAddress) {
          const street = removeExtraSpaces(row?.streetLine1 ?? '');
          const city = removeExtraSpaces(row?.city);
          const unit = removeExtraSpaces(row?.unit);
          const fullAddress = `${row.streetLine1} ${row.city} ${row.state} ${row.zip}`;
          const county = await getCounty(fullAddress);
          row.streetLine1 = street;
          row.city = city;
          row.unit = unit;
          row.county = county;
        }

        let records = Array.from(apiRef.current.getRowModels().values());
        const isCurrentAdressIndex = records?.findIndex((item) => {
          return isAddress ? item.isCurrent : item.isPrimary;
        });

        const currentCell = isAddress ? row.isCurrent : row.isPrimary;
        const isNewRow = isCurrentAdressIndex > -1 && row?.id === newGridRecordId && currentCell;

        if ((isAddress || isEmployment) && (isNewRow || (isCurrentAdressIndex !== -1 && records[isCurrentAdressIndex]?.id !== row?.id && currentCell))) {
          mutateCombinedDti?.();
          setOpenAlert(true);
        } else {
          if (row.id !== newGridRecordId) {
            records = records.map((value) => value.id === row.id ? row : value);
          } else {
            records = records.filter((record) => newGridRecordId !== record.id);
            records.push({ ...row });
          }
          const result = await onChange(records as T[]);
          mutateCombinedDti?.();
          return result;
        }
        mutateCombinedDti?.();
      },
    }),
    [onChange, apiRef.current, mutateCombinedDti],
  );

  const handleSubmit = useCallback((isNoClicked = false) => {
    let records = Array.from(apiRef.current.getRowModels().values());

    if (currentRow?.id !== newGridRecordId) {
      if (isNoClicked) {
        records = records.map((record) =>
          record.id === currentRow?.id
            ? isAddress
              ? { ...currentRow, isCurrent: false, includeInDti: false }
              : { ...currentRow, isPrimary: false }
            : { ...record }
        );
      } else {
        records = records.map((record) =>
          record.id === currentRow?.id
            ? { ...currentRow }
            : isAddress
            ? { ...record, isCurrent: false, includeInDti: false}
            : { ...record, isPrimary: false }
        );
      }
    } else {
      records = records.map((record) =>
        record.id !== newGridRecordId
          ? isAddress
            ? { ...record, ...(!isNoClicked && { isCurrent: false, includeInDti: false}) }
            : { ...record, ...(!isNoClicked && { isPrimary: false }) }
          : isAddress
          ? { ...record, isCurrent: !isNoClicked}
          : { ...record, isPrimary: !isNoClicked }
      );
    }

    onChange(records as T[]);
    mutateCombinedDti?.();
    setOpenAlert(false);
  }, [currentRow, onChange, apiRef.current]);

  return (
    <>
      <PhoenixGrid
        autoHeight
        withHeader
        canNotCopy
        hideFooterPagination
        hideFooter
        isFlexible
        autoRowHeight
        name={name}
        pageSize={10}
        strict={strict}
        columns={columns}
        loading={!data}
        rows={rows}
        actions={gridActions}
        btn={AddButton}
        apiRef={apiRef}
        validateSchema={validateSchema}
        {...rest}
      />
      {openAlert &&
        <AlertModal
          open
          maxWidth="400px"
          title="Alert"
          subtitle={AlertText}
          cancelButtonText="No"
          buttonText="Yes"
          onConfirm={handleSubmit}
          onClose={() => handleSubmit(true)}
        />
      }
    </>
  );
};
